import { BeneficiaryTypes } from 'enrollment/features/AddYourBeneficiaries/services';
import { EnrollmentContext } from 'theSource/queries';
import { ROUTES } from 'enrollment/app/routes';
import { benefitCategoryLookupCodes, ICON_COMPONENT } from './constant';

export const checkForFileSize = (file: any, sizeLimitInMB: number) => {
  const fileSize = file.size;
  const fileMb = fileSize / 1024 ** 2;
  if (fileMb > sizeLimitInMB) {
    alert(`Please upload a file less than ${sizeLimitInMB}MB`);
    return true;
  }
};

export const calculateAllocation = (beneficiaries: BeneficiaryTypes[]) => {
  let total = 0;
  beneficiaries?.forEach((beneficiary: BeneficiaryTypes) => {
    total += parseInt(beneficiary.allocation || '0');
  });
  return total;
};

export const PSDialog = {
  dialogOpen: (elemId: string) => {
    const dialog = document.querySelector(`dialog#${elemId}`);
    if (!dialog) {
      return;
    }
    document.dispatchEvent(new CustomEvent('psOpenDialog', { detail: dialog }));
  },
  dialogClose: (elemId: string, callback?: () => void) => {
    const dialog = document.querySelector(`dialog#${elemId}`);
    if (!dialog) {
      return;
    }
    document.dispatchEvent(new CustomEvent('psCloseDialog', { detail: dialog }));
    setTimeout(() => {
      if (callback) {
        callback();
      }
    }, 750);
  },
};

export const filterBeneficiaries = (
  beneficiaries: BeneficiaryTypes[],
  isPrimary: boolean
) => {
  return beneficiaries?.filter((beneficiary) => {
    return beneficiary.primary_beneficiary === isPrimary;
  });
};

export const checkZeroAllocation = (beneficiaries: BeneficiaryTypes[]) => {
  let zeroAllocation = false;
  beneficiaries?.forEach((beneficiary: BeneficiaryTypes) => {
    beneficiary.allocation === '0' && (zeroAllocation = true);
  });
  return zeroAllocation;
};

export const checkEmptyAllocation = (beneficiaries: BeneficiaryTypes[]) => {
  let emptyAllocation = false;
  beneficiaries?.forEach((beneficiary: BeneficiaryTypes) => {
    beneficiary.allocation === '' && (emptyAllocation = true);
  });
  return emptyAllocation;
};

export const isCoverageDeclined = (coverage: any) => {
  return coverage?.req_coverage_level_lookup_code === 'decline';
};

export const hasOpenLifeEvent = (enrollmentContextData: any) => {
  return (
    enrollmentContextData?.find(
      (context: EnrollmentContext) =>
        (context.enrollment_type_lookup_code === 'life_event' &&
          context.status === 'open') ||
        (context.enrollment_type_lookup_code === 'life_event' &&
          context.subscriber_life_event?.org_plan_year_ids.includes(
            context.org_plan_year_id
          ))
    ) !== undefined
  );
};

export const REQUIRED_TASKS_MAPPER: { [key: string]: string } = {
  recommendation_agreement: 'decision_support',
  personal: '',
  dependents: 'profile',
  confirm: 'details',
  documents: 'details',
};

export const REQUIRED_ROUTES_TASKS_MAPPER: { [key: string]: string } = {
  '': ROUTES.enrollment.profile.path,
  questionnaire: ROUTES.enrollment.questionnaire.path,
  decision_support: ROUTES.enrollment.decisionSupportRouter.path,
  profile: ROUTES.enrollment.profile.path,
  family: ROUTES.enrollment.family.path,
  confirm: ROUTES.enrollment.benefitDetails.path,
  documents: ROUTES.enrollment.benefitDetails.path,
  details: ROUTES.enrollment.benefitDetails.path,
  // these two support old states that are now removed from the backend but may still exist in old enrolment states
  survey: ROUTES.enrollment.questionnaire.path,
};

export interface LastViewedProps {
  currentCategory?: string;
  requiredTask: string;
}

export const getLastViewedPage = ({
  currentCategory,
  requiredTask,
}: LastViewedProps) => {
  if (currentCategory && !['personal', 'dependents'].includes(requiredTask)) {
    return currentCategory;
  } else if (requiredTask !== 'benefits') {
    return REQUIRED_TASKS_MAPPER[requiredTask || ''];
  }
  return 'family';
};

export const getPageRouter = (lastViewedPage: string) => {
  if (lastViewedPage) {
    if (benefitCategoryLookupCodes.includes(lastViewedPage)) {
      return `${ROUTES.enrollment.benefits.path}#${lastViewedPage}`;
    } else {
      return REQUIRED_ROUTES_TASKS_MAPPER[lastViewedPage];
    }
  } else {
    return ROUTES.enrollment.profileEdit.path;
  }
};

export interface IconClassProps {
  benefitType: string;
  isSurvey?: boolean;
}

export function SpecificIcon(props: IconClassProps) {
  if (props.isSurvey) {
    return (
      <i className="ph ph-question-mark benefitIcon" aria-hidden="true"></i>
    );
  }
  const SpecificIcon = ICON_COMPONENT[props.benefitType];
  if (SpecificIcon) {
    return (
      <i className={`ph ${SpecificIcon} benefitIcon`} aria-hidden="true"></i>
    );
  }
  return <i className="ph ph-umbrella benefitIcon" aria-hidden="true"></i>;
}

export const isExternalLink = (url: string) => {
  const tmp = document.createElement('a');
  tmp.href = url;
  return tmp.host !== window.location.host;
};

export const getPlanYears = (
  enrollmentContexts: EnrollmentContext[],
  activeContextIndex: number,
) => {
  const activeContext = enrollmentContexts[activeContextIndex];

  // for overlapping plan years take both plan years
  let planYears = activeContext.included_plan_years;

  // for life events grab only the relevant plan year
  if (activeContext.enrollment_type_lookup_code === 'life_event') {
    planYears = planYears.filter((py) => {
      return activeContext.subscriber_life_event?.org_plan_year_ids.includes(py.id);
    });
  }

  return planYears;
};

export const closeSideDrawer = () => {
  const hasDrawerOpenClass = document.querySelector('.drawer-open');
  if (hasDrawerOpenClass) {
    hasDrawerOpenClass.classList.remove('drawer-open');
  }
};

export const generateRandomSixDigitNumber = (): number => {
  return Math.floor(100000 + Math.random() * 900000);
};

export function determineColor(benefitCategory: string) {
  switch (benefitCategory) {
    case 'category_1':
      return 'annotation5';
    case 'category_2':
      return 'annotation2';
    case 'category_3':
      return 'annotation3';
    default:
      return 'annotation4';
  }
}
